/**
 * Subscription Manager - مدیریت Push Subscriptions
 * این سرویس مسئول ایجاد، ذخیره و حذف subscriptions است
 */

import { VAPID_PUBLIC_KEY, PUSH_WORKER_URL } from './vapidConfig';

class SubscriptionManager {
  constructor() {
    this.subscription = null;
    this.registration = null;
  }

  /**
   * Initialize the subscription manager
   * @returns {Promise<boolean>}
   */
  async initialize() {
    try {
      console.log('🔔 Initializing Subscription Manager...');
      
      // Check if service worker and notifications are supported
      if (!('serviceWorker' in navigator)) {
        console.warn('⚠️ Service Worker not supported');
        return false;
      }
      
      if (!('PushManager' in window)) {
        console.warn('⚠️ Push API not supported');
        return false;
      }
      
      if (!('Notification' in window)) {
        console.warn('⚠️ Notifications not supported');
        return false;
      }
      
      // Wait for service worker to be ready
      this.registration = await navigator.serviceWorker.ready;
      console.log('✅ Service Worker ready');
      
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Subscription Manager:', error);
      return false;
    }
  }

  /**
   * Request notification permission
   * @returns {Promise<string>} - 'granted', 'denied', or 'default'
   */
  async requestPermission() {
    try {
      console.log('📱 Requesting notification permission...');
      
      if (!('Notification' in window)) {
        console.warn('⚠️ Notifications not supported');
        return 'denied';
      }
      
      const permission = await Notification.requestPermission();
      console.log('📱 Permission result:', permission);
      
      return permission;
    } catch (error) {
      console.error('❌ Failed to request permission:', error);
      return 'denied';
    }
  }

  /**
   * Subscribe to push notifications
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<Object|null>} - subscription object or null
   */
  async subscribe(userId = null) {
    try {
      console.log('🔔 Subscribing to push notifications...');
      
      // Initialize if not already
      if (!this.registration) {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize');
        }
      }
      
      // Check permission
      if (Notification.permission !== 'granted') {
        const permission = await this.requestPermission();
        if (permission !== 'granted') {
          console.warn('⚠️ Notification permission not granted');
          return null;
        }
      }
      
      // Check if already subscribed
      const existingSubscription = await this.registration.pushManager.getSubscription();
      if (existingSubscription) {
        console.log('✅ Already subscribed');
        this.subscription = existingSubscription;
        return existingSubscription;
      }
      
      // Convert VAPID key
      const convertedVapidKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      
      // Subscribe
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey,
      });
      
      console.log('✅ Push subscription created:', subscription);
      this.subscription = subscription;
      
      // Send subscription to server
      await this.sendSubscriptionToServer(subscription, userId);
      
      return subscription;
    } catch (error) {
      console.error('❌ Failed to subscribe:', error);
      return null;
    }
  }

  /**
   * Unsubscribe from push notifications
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async unsubscribe(userId = null) {
    try {
      console.log('🔕 Unsubscribing from push notifications...');
      
      // Initialize if not already
      if (!this.registration) {
        const initialized = await this.initialize();
        if (!initialized) {
          throw new Error('Failed to initialize');
        }
      }
      
      // Get existing subscription
      const subscription = await this.registration.pushManager.getSubscription();
      
      if (!subscription) {
        console.log('✅ No subscription found');
        return true;
      }
      
      // Unsubscribe
      const success = await subscription.unsubscribe();
      
      if (success) {
        console.log('✅ Unsubscribed successfully');
        this.subscription = null;
        
        // Remove subscription from server
        await this.removeSubscriptionFromServer(subscription, userId);
      }
      
      return success;
    } catch (error) {
      console.error('❌ Failed to unsubscribe:', error);
      return false;
    }
  }

  /**
   * Get current subscription
   * @returns {Promise<Object|null>}
   */
  async getSubscription() {
    try {
      // Initialize if not already
      if (!this.registration) {
        const initialized = await this.initialize();
        if (!initialized) {
          return null;
        }
      }
      
      const subscription = await this.registration.pushManager.getSubscription();
      this.subscription = subscription;
      return subscription;
    } catch (error) {
      console.error('❌ Failed to get subscription:', error);
      return null;
    }
  }

  /**
   * Check if currently subscribed
   * @returns {Promise<boolean>}
   */
  async isSubscribed() {
    const subscription = await this.getSubscription();
    return subscription !== null;
  }

  /**
   * Send subscription to server
   * @param {Object} subscription - subscription object
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async sendSubscriptionToServer(subscription, userId = null) {
    try {
      console.log('📤 Sending subscription to server...');
      
      // Get user ID if not provided
      if (!userId) {
        userId = this.getUserId();
      }
      
      const response = await fetch(`${PUSH_WORKER_URL}/api/notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription,
          userId: userId,
          timestamp: Date.now(),
          userAgent: navigator.userAgent,
          platform: navigator.platform,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Subscription sent to server:', data);
      
      return true;
    } catch (error) {
      console.error('❌ Failed to send subscription to server:', error);
      return false;
    }
  }

  /**
   * Remove subscription from server
   * @param {Object} subscription - subscription object
   * @param {string} userId - شناسه کاربر
   * @returns {Promise<boolean>}
   */
  async removeSubscriptionFromServer(subscription, userId = null) {
    try {
      console.log('📤 Removing subscription from server...');
      
      // Get user ID if not provided
      if (!userId) {
        userId = this.getUserId();
      }
      
      const response = await fetch(`${PUSH_WORKER_URL}/api/notifications/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription,
          userId: userId,
          timestamp: Date.now(),
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }
      
      const data = await response.json();
      console.log('✅ Subscription removed from server:', data);
      
      return true;
    } catch (error) {
      console.error('❌ Failed to remove subscription from server:', error);
      return false;
    }
  }

  /**
   * Get user ID from localStorage or generate anonymous ID
   * @returns {string}
   */
  getUserId() {
    try {
      // Try to get user from localStorage
      const userProfile = localStorage.getItem('userProfile');
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        return profile.uid || profile.id || 'anonymous';
      }
      
      // Generate anonymous ID if no user found
      let anonymousId = localStorage.getItem('anonymousId');
      if (!anonymousId) {
        anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('anonymousId', anonymousId);
      }
      
      return anonymousId;
    } catch (error) {
      console.error('❌ Failed to get user ID:', error);
      return 'anonymous';
    }
  }

  /**
   * Convert VAPID key from URL-safe base64 to Uint8Array
   * @param {string} base64String
   * @returns {Uint8Array}
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

// Export singleton instance
export const subscriptionManager = new SubscriptionManager();

export default subscriptionManager;



